/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2022. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: HttpExecutor.ts
 * Created by: Kevin
 * Create Date: 2022-12-09 18:19
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import {HttpRequest} from "@/commons/https/HttpRequest";
import axios from "axios";
import Log from "@/commons/logs/Log";
import HttpTokenProvider from "@/commons/https/HttpTokenProvider";
import {StatusDef} from "@/commons/StatusDef";
import Result from "@/commons/Result";
import CheckUtil from "@/commons/utils/CheckUtil";
import {HttpStatus} from "@/commons/https/HttpStatus";
import SwalUtil from "@/commons/utils/SwalUtil";
import SwalConfig from "@/commons/utils/SwalConfig";
import AppConfig from "@/commons/AppConfig";
import {showPermissionNeeded, showSessionExpired} from "@/commons/session/SessionExpired";

// The server's errorStatusCode when IDAM refused the session's token for a permission the route needs
// (StErrPermissionDenied); it rides on a 403.
const StErrPermissionDenied = -89984;

export default class HttpExecutor {

    // ------------------------------------------------------------------------------------
    // singleton
    // ------------------------------------------------------------------------------------
    private static _instance: HttpExecutor;

    public static get Instance() {
        return this._instance || (this._instance = new this())
    }


    // ------------------------------------------------------------------------------------
    // static functions
    // ------------------------------------------------------------------------------------

    private static FormatError(msg?: string, err?: any): string {
        if (CheckUtil.isEmptyString(msg) && err == null) {
            return "unknown error";
        }
        if (err == null) {
            return <string>msg;
        }
        return msg + ", cause: " + err;
    }

    // ------------------------------------------------------------------------------------
    // properties
    // ------------------------------------------------------------------------------------

    private readonly permissionCheckExcludedURLs: string[]
    private _permissionDeniedHandler: Function | null;

    // ------------------------------------------------------------------------------------
    // constructor
    // ------------------------------------------------------------------------------------

    private constructor() {
        this.permissionCheckExcludedURLs = [
            "/account/validate/emailpwd",
            "/account/signup",
            "/validate/refreshtkn",
            '/logout',
        ];
        this._permissionDeniedHandler = null;
    }

    // ------------------------------------------------------------------------------------
    // public functions
    // ------------------------------------------------------------------------------------

    public initialize(): Promise<Result<void>> {
        return new Promise((resolve) => {
            try {
                axios.interceptors.response.use(
                    (rsp) => rsp,
                    (err) => {
                        const {config} = err;
                        if (config == null) {
                            return Promise.reject(err);
                        }
                        // NOTE: auth rides on the session cookie (withCredentials); there is no
                        // client-side token refresh. A 401 always means re-authentication.
                        if (err.response != null && err.response.status === HttpStatus.StatusUnauthorized) {
                            this.onPermissionDenied(err);
                        }
                        return Promise.reject(err);
                    }
                );

                Log.debug("initialize http executor ... ok!");
                resolve(Result.of<void>(StatusDef.Success));
            } catch (e) {
                Log.error("error initializing http executor: " + e);
                resolve(Result.of<void>(StatusDef.ErrError));
            }
        });
    }

    public async execute(req: HttpRequest, tkn?: HttpTokenProvider): Promise<any> {
        try {
            return await req.execute(tkn);
        } catch (err: any) {
            // The server answers 302 when the session cannot be renewed. One handler says so, once
            // (commons/session/SessionExpired.ts explains why there is only one).
            if (err?.response?.status === HttpStatus.StatusFound) {
                showSessionExpired();
            }
            // IDAM refused the token team-site forwarded for this session, and the server passed the
            // refusal through (SG-TeamSite-Sign-In-Again-On-Missing-Scope-20261015). 401: the token is
            // expired or invalid, so nothing works until the user signs in again. 403 with
            // StErrPermissionDenied: the token lacks a permission this request needs; signing in again
            // picks up a permission granted or renamed since, so say so, but let the reader close it.
            // The sign-in-again flow's own logout is left out: it is already on its way out.
            if (!this.isPermissionCheckExclude(req.getTargetURL())) {
                if (err?.response?.status === HttpStatus.StatusUnauthorized) {
                    showSessionExpired();
                } else if (err?.response?.status === HttpStatus.StatusForbidden
                    && err?.response?.data?.errorStatusCode === StErrPermissionDenied) {
                    showPermissionNeeded();
                }
            }
            // The host names a team the server does not have — a stale bookmark, a deleted team, a
            // typo in the subdomain. Saying "expired" would send the reader to look at their
            // session, which is fine; the only thing that helps is signing up, and the server says
            // where. Without this branch the dialog closed onto a blank page.
            if (err?.response?.status === HttpStatus.StatusNotFound
                && err?.response?.data?.code === "TEAM_NOT_FOUND") {
                SwalUtil.popupNotification(SwalConfig.errorInfo(
                    "Error",
                    "This team does not exist. Taking you to the signup page.",
                    "Cancel")
                ).finally(() => {
                    window.location.href = HttpExecutor.resolveSafeRedirectUrl(err?.response?.data?.redirectUrl);
                })
            }
            throw err;
        }
    }

    /**
     * Validate a server-supplied redirect target: only same-origin URLs or URLs on the
     * configured auth site are honored, anything else falls back to the auth site.
     * Prevents an attacker-influenced response body from becoming an open redirect.
     */
    public static resolveSafeRedirectUrl(candidate: unknown): string {
        const fallback = AppConfig.Instance.authSiteURL + AppConfig.Instance.apiURLServiceTeam;
        if (typeof candidate !== "string" || candidate.length === 0) {
            return fallback;
        }
        try {
            const url = new URL(candidate, window.location.origin);
            if (url.protocol !== "https:" && url.protocol !== "http:") {
                return fallback;
            }
            const authOrigin = new URL(AppConfig.Instance.authSiteURL).origin;
            if (url.origin === window.location.origin || url.origin === authOrigin) {
                return url.href;
            }
        } catch {
            // fall through to fallback
        }
        return fallback;
    }

    public async resulting<T>(req: HttpRequest, errMsg?: string): Promise<Result<T>> {
        return this.resultingWithToken<T>(req, HttpTokenProvider.TeamService, errMsg)
    }

    public async resultingWithToken<T>(req: HttpRequest, tkn?: HttpTokenProvider, errMsg?: string): Promise<Result<T>> {
        let token: HttpTokenProvider;
        if (tkn == null) {
            token = HttpTokenProvider.TeamService;
            Log.debug(">>>>>>> token is nil, use team service token provider to instead.");
        } else {
            token = tkn;
        }

        return new Promise<Result<T>>((resolve) => {
            HttpExecutor.Instance.execute(req, token)
                .then((rsp) => {
                    if (!rsp) {
                        const e = HttpExecutor.FormatError(
                            errMsg,
                            "error on execute http request(" + req.getTargetURL() + "), cause: rsp is null"
                        );
                        Log.error(e);
                        resolve(Result.of<T>(StatusDef.ErrError, null, null, e));
                        return;
                    }

                    const body = rsp?.data?.body ?? rsp?.data ?? null;

                    resolve(Result.of<T>(StatusDef.Success, body as T, rsp, null));
                })
                .catch((err) => {
                    const e = HttpExecutor.FormatError(
                        errMsg,
                        "error on execute http request(" + req.getTargetURL() + "): " + err
                    );
                    Log.error(e);
                    resolve(Result.of<T>(StatusDef.ErrError, null, null, e));
                    return;
                });
        });
    }

    // ------------------------------------------------------------------------------------
    // private functions
    // ------------------------------------------------------------------------------------

    private isPermissionCheckExclude(url: string): boolean {
        for (let i = 0; i < this.permissionCheckExcludedURLs.length; i++) {
            if (url.includes(this.permissionCheckExcludedURLs[i])) {
                return true;
            }
        }
        return false;
    }

    /*private onRefreshTokenFailed() {
        OnlineUserManager.Instance.logout();
        PageHelper.Instance.goToLogin();
    }*/

    private onPermissionDenied(err: any) {
        if (this._permissionDeniedHandler != null) {
            this._permissionDeniedHandler(err);
        } else {
            if (err?.response?.status === HttpStatus.StatusFound) {
                showSessionExpired();
            }
        }
    }

    // ------------------------------------------------------------------------------------
    // get/set functions
    // ------------------------------------------------------------------------------------

    get permissionDeniedHandler(): Function | null {
        return this._permissionDeniedHandler;
    }

    set permissionDeniedHandler(value: Function | null) {
        this._permissionDeniedHandler = value;
    }
}
